<script setup lang="ts">
/**
 * 左ペインと右ペインの境界。ドラッグして左ペインの幅を変えられる。
 *
 * 画面サイズ・解像度が利用者ごとにバラバラなため、最適な幅は固定値では決まらない。
 * 最後は本人に調整してもらえるようにし、結果は localStorage に保存する。
 *
 * pointerイベント + setPointerCapture を使うことで、ドラッグ中にカーソルが
 * iframe や他要素の上へ抜けても追従が途切れない。
 */
import { ref } from 'vue'
import { useLayoutStore, type ScreenKey } from '@/stores/layout'

const props = defineProps<{ screen: ScreenKey }>()
const layout = useLayoutStore()
const dragging = ref(false)

function onPointerDown(e: PointerEvent) {
  dragging.value = true
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  // ドラッグ中にテキストが選択されるのを防ぐ
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
}

function onPointerMove(e: PointerEvent) {
  if (!dragging.value) return
  // ワークスペースの左端からの距離がそのまま左ペインの幅になる
  const host = (e.currentTarget as HTMLElement).parentElement
  const left = host?.getBoundingClientRect().left ?? 0
  layout.setLeftWidth(props.screen, e.clientX - left)
}

function onPointerUp(e: PointerEvent) {
  if (!dragging.value) return
  dragging.value = false
  ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
  layout.persist()
}

/** ダブルクリックで既定幅に戻す */
function onDoubleClick() {
  layout.setLeftWidth(props.screen, 970)
  layout.persist()
}
</script>

<template>
  <div
    class="pane-divider"
    :class="{ dragging }"
    title="ドラッグで幅を調整 / ダブルクリックで既定に戻す"
    role="separator"
    aria-orientation="vertical"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @dblclick="onDoubleClick"
  >
    <span class="pane-divider-grip"></span>
  </div>
</template>
